import {
  MonitoringPromptRunUnitType,
  MonitoringPromptRunVerdictGrade,
  SyncCoreDataDefinitionsEnumTranslator,
} from '@edgebox/sync-core-data-definitions';
import { ISyncCoreApi } from '@edgebox/sync-core-rest-client';
import { faXmark } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import React from 'react';
import { TaxonomyTermName } from '../Onboarding/ContentInventory/Components/TaxonomyTermNameBadge';
import {
  AnswerPassage,
  PASSAGE_VERDICT_HINTS,
  PASSAGE_VERDICT_LABELS,
  PassageBrand,
  PassageSource,
  verdictGradeScore,
} from './answerPassages.utils';
import { toneForMetric } from './bandVisuals.utils';
import { SentimentThumb } from './SentimentThumb';
import { SourceFavicon } from './SourceFavicon';

/** Which of the answer's measurements the purpose lets the card show. */
export interface PassageCardMeasures {
  verdict: boolean;
  position: boolean;
  recommendation: boolean;
  attributes: boolean;
}

/** The card: white, lifted well off the page by a strong shadow, and edged with a crisp ring. */
const PASSAGE_CARD_CLASSES =
  'rounded-xl bg-white p-3.5 text-sm ring-1 ring-slate-900/10 shadow-[0_18px_40px_-12px_rgba(15,23,42,0.3),0_4px_12px_-4px_rgba(15,23,42,0.12)]';

/** A small tag beside a brand's name in the card. */
const BRAND_TAG_CLASSES = 'inline-flex items-center rounded-md px-1.5 py-px text-[0.6875rem] leading-4';

/** The dot beside a brand the page has no colour for. */
const UNCOLOURED_BRAND_DOT = '#94a3b8';

/**
 * Passages whose opening words are a name rather than a statement — a table row's label, usually the
 * attribute it rates, and a heading — so the card shows them as a headline, not as a quotation.
 */
const HEADLINE_PASSAGE_TYPES: ReadonlySet<MonitoringPromptRunUnitType> = new Set([
  MonitoringPromptRunUnitType.TableRow,
  MonitoringPromptRunUnitType.Heading,
]);

/**
 * What one passage of the answer says, next to the answer: the brands it is about, the verdict it
 * gives each and the attributes it discusses each on, then the context — what it compares, the tags
 * it carries, the alternatives it names that nobody tracks, and the sources behind it.
 *
 * Shown for the passage under the pointer and kept for the one the reader clicked. The purpose's
 * mask applies here as everywhere on the page: a measurement the purpose does not take is absent.
 */
export function AnswerPassageCard({
  api,
  passage,
  excerpt,
  pinned,
  onUnpin,
  nameOf,
  colorOf,
  measures,
}: {
  api?: ISyncCoreApi;
  passage: AnswerPassage;
  /** The first words of the passage, so the reader sees which one the card is about. */
  excerpt: string;
  /** Whether the reader pinned this passage, which makes the card stay and its links usable. */
  pinned: boolean;
  /** Lets go of the passage; `byKeyboard` when the reader did it from the keyboard, whose focus has to go somewhere. */
  onUnpin: (byKeyboard: boolean) => void;
  /** The brand's display name — "You" for the reader's own — when the page knows it. */
  nameOf: (termKey: string) => string | undefined;
  colorOf: (termKey: string) => string | undefined;
  measures: PassageCardMeasures;
}) {
  const kind = passage.type
    ? SyncCoreDataDefinitionsEnumTranslator.transLateEnumValue('MonitoringPromptRunUnitType', passage.type)
    : undefined;
  const brandName = (termKey: string) => nameOf(termKey) ?? <TaxonomyTermName api={api} termKey={termKey} />;
  const hasContext =
    !!passage.comparison || passage.untrackedCoSubjects.length > 0 || passage.tagTermKeys.length > 0 || passage.sources.length > 0;

  return (
    <section aria-label="What this passage says" className={PASSAGE_CARD_CLASSES}>
      <div className="d-flex align-items-start justify-content-between gap-2 mb-2">
        <div className="min-w-0">
          <div className="text-[11px] uppercase tracking-wider text-slate-400">{kind ?? 'Passage'}</div>
          {excerpt ? (
            passage.type && HEADLINE_PASSAGE_TYPES.has(passage.type) ? (
              <div className="font-semibold text-slate-800 truncate" title={excerpt}>
                {excerpt}
              </div>
            ) : (
              <div className="text-slate-600 italic truncate" title={excerpt}>
                “{excerpt}”
              </div>
            )
          ) : null}
        </div>
        {pinned ? (
          <button
            type="button"
            className="flex-shrink-0 rounded px-1.5 text-slate-400 hover:text-slate-700 hover:bg-slate-100"
            // A click the keyboard made (Enter or Space) counts no presses; a pointer's counts at least one.
            onClick={(event) => onUnpin(event.detail === 0)}
            aria-label="Unpin this passage"
            title="Unpin this passage"
          >
            <FontAwesomeIcon icon={faXmark} aria-hidden />
          </button>
        ) : (
          <span className="flex-shrink-0 text-[11px] text-slate-400 whitespace-nowrap">Click to pin</span>
        )}
      </div>

      {passage.brands.length ? (
        <ul className="list-none p-0 m-0 space-y-2.5">
          {passage.brands.map((brand) => (
            <PassageBrandRow
              key={brand.termKey}
              api={api}
              brand={brand}
              name={brandName(brand.termKey)}
              color={colorOf(brand.termKey)}
              recommended={passage.recommendsTermKey === brand.termKey}
              measures={measures}
            />
          ))}
        </ul>
      ) : (
        <div className="text-slate-500">No tracked brand in this passage.</div>
      )}

      {hasContext ? (
        <div className="mt-3 pt-2 border-t border-slate-100 space-y-1.5 text-xs text-slate-600">
          {passage.comparison ? (
            <div>
              {passage.comparison.explicit ? 'Compares with ' : 'Implies a comparison with '}
              {passage.comparison.counterpartTermKey ? (
                <span className="font-semibold">{brandName(passage.comparison.counterpartTermKey)}</span>
              ) : passage.comparison.counterpartUntracked ? (
                'an alternative you do not track'
              ) : (
                'another option'
              )}
            </div>
          ) : null}
          {passage.untrackedCoSubjects.length ? <div>Also names, not tracked: {passage.untrackedCoSubjects.join(', ')}</div> : null}
          {passage.tagTermKeys.length ? (
            <div className="d-flex flex-wrap align-items-center gap-1">
              <span className="text-slate-400">Tags</span>
              {passage.tagTermKeys.map((termKey) => (
                <span key={termKey} className="rounded bg-slate-100 px-1.5 py-0.5">
                  <TaxonomyTermName api={api} termKey={termKey} />
                </span>
              ))}
            </div>
          ) : null}
          {passage.sources.length ? (
            <div className="d-flex flex-wrap align-items-center gap-1">
              <span className="text-slate-400">Sources</span>
              {passage.sources.map((source) => (
                <PassageSourceLink key={source.url} api={api} source={source} focusable={pinned} />
              ))}
            </div>
          ) : null}
        </div>
      ) : null}
    </section>
  );
}

/**
 * One source behind the passage, drawn like every other source on the page: the site's favicon, then
 * what the source reads as, ended with an ellipsis where it runs past the chip. The full title and the
 * address are in its tooltip.
 */
function PassageSourceLink({ api, source, focusable }: { api?: ISyncCoreApi; source: PassageSource; focusable: boolean }) {
  return (
    <a
      href={source.url}
      target="_blank"
      rel="noopener noreferrer"
      className="inline-flex items-center gap-1 max-w-full rounded-md border border-slate-200 bg-white px-2 py-0.5 text-slate-600 hover:border-sky-300 hover:text-sky-800"
      title={source.title && source.title !== source.label ? `${source.title} — ${source.url}` : source.url}
      // Reachable by keyboard only once pinned: a preview follows the focus and would move away first.
      tabIndex={focusable ? 0 : -1}
    >
      <SourceFavicon api={api} domain={source.host} />
      <span className="max-w-[10rem] truncate">{source.label}</span>
    </a>
  );
}

/** One brand of the passage: its verdict here, whether it is named first here, and its attributes. */
function PassageBrandRow({
  api,
  brand,
  name,
  color,
  recommended,
  measures,
}: {
  api?: ISyncCoreApi;
  brand: PassageBrand;
  name: React.ReactNode;
  color?: string;
  recommended: boolean;
  measures: PassageCardMeasures;
}) {
  const showFirstMention = measures.position && brand.firstMention;
  const showRecommended = measures.recommendation && recommended;
  const showAttributes = measures.attributes && brand.attributeTermKeys.length > 0;

  return (
    <li className="flex items-start justify-between gap-3">
      {/* The name and its tags run on one line, wrapping only when they must. */}
      <div className="flex min-w-0 flex-wrap items-center gap-x-1.5 gap-y-1">
        <span
          aria-hidden
          className="inline-block h-2 w-2 flex-shrink-0 rounded-full"
          // The brand's own colour, known only at runtime.
          style={{ backgroundColor: color ?? UNCOLOURED_BRAND_DOT }}
        />
        <span className="min-w-0 truncate font-semibold text-slate-800">{name}</span>
        {showRecommended ? (
          <span className={`${BRAND_TAG_CLASSES} bg-emerald-50 font-semibold text-emerald-700`}>AI recommends it here</span>
        ) : null}
        {showFirstMention ? <span className={`${BRAND_TAG_CLASSES} bg-sky-50 text-sky-700`}>First mention</span> : null}
        {showAttributes
          ? brand.attributeTermKeys.map((termKey) => (
              <span key={termKey} className={`${BRAND_TAG_CLASSES} bg-slate-100 text-slate-600`}>
                <TaxonomyTermName api={api} termKey={termKey} />
              </span>
            ))
          : null}
      </div>
      {measures.verdict ? <PassageVerdict grade={brand.verdict} /> : null}
    </li>
  );
}

/**
 * The verdict one passage gives one brand: its thumb and its grade. A brand the verdict judge never
 * graded here says so — it is never shown as neutral.
 */
function PassageVerdict({ grade }: { grade?: MonitoringPromptRunVerdictGrade }) {
  if (grade === undefined) {
    return (
      <span className="text-[11px] text-slate-400 whitespace-nowrap" title="The verdict was not graded for this brand in this passage.">
        Not graded
      </span>
    );
  }
  return (
    <span className="d-inline-flex align-items-center gap-1 text-xs text-slate-600 whitespace-nowrap">
      <SentimentThumb
        tone={toneForMetric('verdict', verdictGradeScore(grade))}
        metric="verdict"
        description={PASSAGE_VERDICT_HINTS[grade]}
      />
      {PASSAGE_VERDICT_LABELS[grade]}
    </span>
  );
}
